import { Check, Copy } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Valor em claro da chave -- exibido uma unica vez. */
  apiKey: string | null;
}

export function ApiKeyRevealDialog({ open, onOpenChange, apiKey }: Props) {
  const { t } = useTranslation("serviceUsers");
  const [copied, setCopied] = useState(false);

  async function copy() {
    if (!apiKey) return;
    try {
      await navigator.clipboard.writeText(apiKey);
      setCopied(true);
      toast.success(t("reveal.copied"));
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error(t("reveal.copy"));
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("reveal.title")}</DialogTitle>
          <DialogDescription>{t("reveal.description")}</DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-2">
          <Input
            value={apiKey ?? ""}
            readOnly
            onFocus={(e) => e.currentTarget.select()}
            className="font-mono text-xs"
          />
          <Button type="button" variant="outline" onClick={copy}>
            {copied ? (
              <Check className="h-4 w-4" />
            ) : (
              <Copy className="h-4 w-4" />
            )}
            {t("reveal.copy")}
          </Button>
        </div>
        <DialogFooter>
          <Button type="button" onClick={() => onOpenChange(false)}>
            {t("reveal.done")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
